import { BsFileEarmarkPdfFill } from 'react-icons/bs';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CallToAction from '../calltoaction/CallToAction';
import Button from '../../../common/Button';
import { slugify, deslugify } from '../../../utils/slug';
import { fetchInternationalPackages } from '../../../utils/api';

const PlacePage = () => {
  const { countrySlug, placeSlug } = useParams();
  const countryName = deslugify(countrySlug);
  const placeName = deslugify(placeSlug);

  const [placeData, setPlaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const MINIMUM_LOAD_TIME = 0;

    const fetchPlaceData = async () => {
      const startTime = Date.now();
      try {
        setLoading(true);
        const data = await fetchInternationalPackages({
          titleSlug: placeSlug,
          categorySlug: countrySlug,
        });

        const place = data.find(
          (item) =>
            item.type === 'subcategory' &&
            slugify(item.title || '') === placeSlug &&
            slugify(item.category?.name || '') === countrySlug,
        );

        if (place) {
          setPlaceData({
            title: place.title,
            content: place.content,
            sliderImages: place.sliderImages.map(
              (img) => `${process.env.REACT_APP_API_BASE_URL}/${img}`,
            ),
            packages: place.packages.map((pkg) => `${process.env.REACT_APP_API_BASE_URL}/${pkg}`),
          });
        } else {
          setError('Place not found');
        }

        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = MINIMUM_LOAD_TIME - elapsedTime;
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
      } catch (err) {
        setError('Failed to load place data. Please try again later.');
        console.error('Error fetching place data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaceData();
  }, [countryName, placeName]);

  useEffect(() => {
    if (placeData?.sliderImages?.length) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % placeData.sliderImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [placeData?.sliderImages?.length]);

  // Skeleton Loader Component for Cards
  const SkeletonCard = () => (
    <div className='relative w-full animate-pulse rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'>
      <div className='relative'>
        <div className='h-[220px] w-full rounded-lg bg-gray-200'></div>
        <div className='absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200'></div>
      </div>
      <div className='mt-2 flex flex-col items-center p-3 text-center'>
        <div className='h-6 w-3/4 rounded bg-gray-200'></div>
      </div>
      <div className='mt-2 h-10 w-full rounded bg-gray-200'></div>
    </div>
  );

  if (loading) {
    return (
      <div className='animate-pulse'>
        <div className='relative h-[30vh] w-full bg-gray-200 lg:h-[70vh]'></div>
        <div className='relative z-10 mx-auto -mt-10 px-4 lg:px-16'>
          <div className='flex justify-center gap-4 rounded-lg bg-white/70 p-4 shadow-lg backdrop-blur-md'>
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className='h-20 w-32 rounded-lg border-2 border-gray-200 bg-gray-200'
              ></div>
            ))}
          </div>
        </div>
        <div className='mx-auto px-4 pt-10 lg:px-16'>
          <div className='mx-auto mb-6'>
            <div className='flex flex-col items-start justify-between lg:flex-row'>
              <div className='order-1 lg:order-1'>
                <div className='mb-4 h-8 w-48 rounded bg-gray-200'></div>
                <div className='h-10 w-32 rounded-lg bg-gray-200'></div>
              </div>
              <div className='order-3 mt-8 lg:order-2 lg:ml-10 lg:mt-0'>
                <div className='mb-4 h-8 w-48 rounded bg-gray-200'></div>
                <div className='flex flex-col gap-4'>
                  {[...Array(2)].map((_, index) => (
                    <div
                      key={index}
                      className='flex h-14 w-full items-center gap-3 rounded-lg bg-gray-200 p-4'
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className='order-2 space-y-4 lg:order-3'>
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className='h-4 w-full rounded bg-gray-200'
              ></div>
            ))}
          </div>
          <div className='mt-6 h-10 w-32 rounded-lg bg-gray-200'></div>
        </div>
      </div>
    );
  }

  if (error || !placeData) {
    return <div className='py-20 text-center text-red-500'>{error || 'Place not found'}</div>;
  }

  return (
    <div className=''>
      <div className='relative h-[30vh] w-full lg:h-[70vh]'>
        <img
          src={placeData.sliderImages[activeIndex]}
          alt='hero'
          className='h-full w-full object-cover brightness-75 transition-all duration-700'
        />
      </div>
      <div className='relative z-10 mx-auto -mt-10 px-4 lg:px-16'>
        <div className='flex justify-center gap-4 rounded-lg bg-white/70 p-4 shadow-lg backdrop-blur-md'>
          {placeData.sliderImages.map((img, index) => (
            <div
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-20 w-32 cursor-pointer overflow-hidden rounded-lg border-2 shadow-sm transition-all ${
                index === activeIndex
                  ? 'scale-105 border-[#3F5F7D]'
                  : 'border-gray-200 hover:scale-105'
              }`}
            >
              <img
                src={img}
                alt={`thumb-${index}`}
                className='h-full w-full object-cover'
              />
            </div>
          ))}
        </div>
      </div>
      <div className='mx-auto px-4 pt-10 lg:px-16'>
        <div className='mx-auto mb-6'>
          <div className='flex flex-col items-start justify-between lg:flex-row'>
            <div className='order-1 lg:order-1'>
              <h1 className='mb-4 text-2xl font-extrabold uppercase text-gray-900 lg:text-3xl'>
                {placeName}
              </h1>
              <button className='rounded-lg bg-black px-11 py-3 font-semibold text-white shadow-md'>
                Overview
              </button>
            </div>
            {placeData.packages.length > 0 && (
              <div className='order-3 mt-8 lg:order-2 lg:ml-10 lg:mt-0'>
                <h1 className='mb-4 text-2xl font-extrabold uppercase text-gray-900 lg:text-3xl'>
                  List of Packages
                </h1>
                <div className='flex flex-col gap-4'>
                  {placeData.packages.map((pkg, index) => (
                    <a
                      key={index}
                      href={pkg}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center gap-3 rounded-lg bg-white p-4 shadow-md transition hover:bg-gray-100'
                    >
                      <BsFileEarmarkPdfFill
                        size={22}
                        className='text-[#FF0000]'
                      />
                      <span className='text-lg font-semibold text-gray-900'>
                        {pkg.split('/').pop()?.replace('.pdf', '')}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div
          className='order-2 space-y-6 text-justify text-[17px] leading-relaxed text-gray-900 lg:order-3'
          dangerouslySetInnerHTML={{ __html: placeData.content }}
        />
        <div className='mb-8 w-fit self-start'>
          <Button category={placeData} />
        </div>
      </div>
      <div className=''>
        <CallToAction countryName={placeName} />
      </div>
    </div>
  );
};

export default PlacePage;
